import { Box, Button, Divider, IconButton, Paper, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { useSelector, useDispatch } from 'react-redux';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PATHS } from 'routes/constants';
import { tableStructure } from './tableStructure';
import {
	getUserRolesGrouped,
	getUserRolesGroupedLoading,
	getUserRolesByUserId,
	getRoles,
} from '../../redux/reducers/userRoles/selectors';
import {
	assignUserRolesRequest,
	getUserRolesGroupedRequest,
	getUserRolesByUserIdRequest,
	removeUserRoleRequest,
	getRolesRequest,
} from '../../redux/reducers/userRoles/reducer';
import { TUserRolesGroupedItemTable } from './types';
import { PermitionItem } from './PermitionItem';
import { ESortDirection } from '../../ui/EasTable/types';
import { TRole } from '../../redux/reducers/userRoles/types';
import { saveUrl2 } from '../../redux/reducers/auth/reducer';
import { ConnectedEmerchants } from '../../layouts/Header/UserMenu/ConnectedEmerchants';
import { UserAvatar } from '../../ui/UserAvatar';
import { getUserStatistic } from '../../redux/reducers/errorCorrections/selectors';
import { UserStatisticStatus } from '../../redux/reducers/errorCorrections/constants';
import { selectUserMgmtConnectedEmerchants } from '../../redux/reducers/userMgmtConnectedEmerchants/selectors';
import { getEMerchantRequest } from '../../redux/reducers/monthlyFee/reducer';
import { getEmManagementListRequest } from '../../redux/reducers/emManagement/reducer';
import {
	getEmManagementListData,
	getEmManagementListLoading,
} from '../../redux/reducers/emManagement/selectors';
import {
	ArrowForward,
	ArrowForwardIos,
	AssignmentOutlined,
	LinkOutlined,
} from '@mui/icons-material';
import { getUserStatisticRequest } from '../../redux/reducers/errorCorrections/reducer';

export const UserManagement = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const users = useSelector(getUserRolesGrouped);
	const usersLoading = useSelector(getUserRolesGroupedLoading);
	const userRolesDetailed = useSelector(getUserRolesByUserId);
	const errorCorrectionsStatistic = useSelector(getUserStatistic);
	const connectedEmerchants = useSelector(selectUserMgmtConnectedEmerchants);
	const emManagementList = useSelector(getEmManagementListData);
	const emManagementListLoading = useSelector(getEmManagementListLoading);
	const [searchParams, setSearchParams] = useSearchParams();
	const selectedUserRealId = searchParams.get('userId');

	const selectedUserIndex = useMemo(() => {
		if (!selectedUserRealId || !users) return null;
		const index = users.data.findIndex((user) => user.userId === selectedUserRealId);
		return index >= 0 ? index : null;
	}, [selectedUserRealId, users]);

	const roles = useSelector(getRoles);

	const usersTable = useMemo(() => {
		if (!users) return null;
		const tableData = {
			...users,
			data: users.data.map((user, index) => ({
				id: index,
				user: {
					id: user.userId,
					fullName: user.fullName,
					email: user.email,
				},

				fullName: user.fullName,
				email: user.email,
				admin: !!user.roles.find((role) => role.roleName === 'admin'),
				resources: !!user.roles.find((role) => role.roleName === 'resources'),
				monthly_fee: !!user.roles.find((role) => role.roleName === 'monthly_fee'),
				vat_reports: !!user.roles.find((role) => role.roleName === 'vat_reports'),
				'swet-bank-integration': !!user.roles.find(
					(role) => role.roleName === 'swet-bank-integration',
				),
				'swisspost-clients': !!user.roles.find((role) => role.roleName === 'swisspost-clients'),
				error_correction: !!user.roles.find((role) => role.roleName === 'error_correction'),
			})),
		};
		return tableData;
	}, [users]);

	useEffect(() => {
		dispatch(getRolesRequest());
		dispatch(
			getUserRolesGroupedRequest({
				pageSize: 100,
				pageNumber: 1,
				orderBy: [{ fieldName: 'fullName', direction: ESortDirection.ASC }],
			}),
		);
	}, [dispatch]);

	useEffect(() => {
		if (!selectedUserRealId) return;
		dispatch(getUserRolesByUserIdRequest({ userId: selectedUserRealId }));
		dispatch(
			getEmManagementListRequest({
				filters: [{ fieldName: 'supportManager', value: selectedUserRealId, operator: '=' }],
				pageSize: 5,
				pageNumber: 1,
			}),
		);
		dispatch(getUserStatisticRequest());
	}, [dispatch, selectedUserRealId]);

	const handleSelectUser = (id: number) => {
		if (!users) return;
		const user = users.data[id];
		if (user) {
			setSearchParams({ userId: user.userId }, { replace: true });
		}
	};

	const assignedPermissions = useMemo(() => {
		return userRolesDetailed || [];
	}, [userRolesDetailed]);

	const availablePermissions = useMemo(() => {
		if (!roles) return [];
		const assignedRoleNames = assignedPermissions.map((r) => r.roleName);
		return roles.filter((role) => !assignedRoleNames.includes(role.roleName));
	}, [roles, assignedPermissions]);

	const handleAction = (id: number, type: string) => {
		if (type === 'Change History') {
			if (!users) return;
			const user = users.data[id];
			if (!user) return;

			dispatch(saveUrl2(window.location.pathname + window.location.search));

			navigate(
				PATHS.USER_MANAGEMENT_CHANGE_HISTORY_PAGE.replace(':userId', user.userId ?? String(id)),
			);
		}
	};

	const onDragEnd = (result: DropResult) => {
		const { source, destination } = result;

		if (!destination) return;

		if (source.droppableId === destination.droppableId) return;

		if (source.droppableId === 'assigned' && destination.droppableId === 'available') {
			const roleToRemove = assignedPermissions[source.index];
			if (roleToRemove) {
				if (selectedUserRealId) {
					dispatch(removeUserRoleRequest({ id: roleToRemove.id, userId: selectedUserRealId }));
				}
			}
		} else if (source.droppableId === 'available' && destination.droppableId === 'assigned') {
			const roleToAdd = availablePermissions[source.index];
			if (roleToAdd && selectedUserRealId) {
				dispatch(assignUserRolesRequest({ userId: selectedUserRealId, roleId: roleToAdd.id }));
			}
		}
	};

	const handleRemoveClick = (role: TRole) => {
		if (!selectedUserRealId) return;
		dispatch(removeUserRoleRequest({ id: role.id, userId: selectedUserRealId }));
	};

	const handleAddClick = (role: TRole) => {
		if (!selectedUserRealId) return;
		dispatch(assignUserRolesRequest({ userId: selectedUserRealId, roleId: role.id }));
	};

	return (
		<Box
			sx={{
				p: '0 1rem',
				display: 'flex',
				flexDirection: 'row',
				width: '100%',
				gap: 2,
				justifyContent: 'center',
			}}
		>
			<Box sx={{ flex: 1, maxWidth: '1000px' }}>
				{/* <Typography variant="h5" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
					User Management
				</Typography> */}
				<EasTableWithoutFilter<TUserRolesGroupedItemTable>
					tableStructure={tableStructure}
					items={usersTable}
					itemsIsLoading={usersLoading}
					handleAction={handleAction}
					handleLink={handleSelectUser}
					highlightedRow={selectedUserIndex}
				>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							gap: 1,
							flexWrap: 'wrap',
						}}
					>
						<Typography variant="body1" color="primary" fontWeight="bold">
							User Management
						</Typography>
						<Button
							component={Link}
							to={PATHS.TASK_TRACKER_PAGE}
							variant="outlined"
							size="small"
							startIcon={<AssignmentOutlined />}
						>
							Task Tracker
						</Button>
					</Box>
				</EasTableWithoutFilter>
			</Box>
			{selectedUserIndex !== null && (
				<Box sx={{ flex: 2, maxWidth: '1000px', display: 'flex', flexDirection: 'column', gap: 2 }}>
					<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
						<Paper
							sx={{
								display: 'flex',
								flexDirection: 'column',
								borderRadius: '12px',
								p: 2,
								flex: 1,
								height: '100%',
							}}
						>
							<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
								<UserAvatar
									user={
										users?.data[selectedUserIndex]
											? {
													id: users.data[selectedUserIndex].userId,
													email: users.data[selectedUserIndex].email,
													fullName: users.data[selectedUserIndex].fullName,
												}
											: null
									}
									size="sm"
								/>
								<Typography variant="body1" color="primary" fontWeight="bold" sx={{ ml: 1 }}>
									{users?.data[selectedUserIndex]?.fullName}
								</Typography>
								<Typography variant="body1" color="primary" fontWeight="bold" sx={{ ml: 1 }}>
									{users?.data[selectedUserIndex]?.email}
								</Typography>
							</Box>
							<Divider sx={{ my: 1 }} />
							<Typography
								variant="body1"
								color="primary"
								fontWeight="bold"
								sx={{ fontSize: '0.85rem' }}
							>
								Count of Not Fixed issues in Error Correction:{' '}
								<span
									style={{
										fontSize: '1.2rem',
										marginLeft: '0.2rem',
										color:
											errorCorrectionsStatistic?.find((user) => user.user.id === selectedUserRealId)
												?.status === UserStatisticStatus.Critical
												? 'red'
												: 'green',
									}}
								>
									{errorCorrectionsStatistic?.find((user) => user.user.id === selectedUserRealId)
										?.issuesCount || 0}
								</span>
								{(errorCorrectionsStatistic?.find((user) => user.user.id === selectedUserRealId)
									?.issuesCount ?? 0) > 0 && (
									<IconButton
										onClick={() =>
											navigate(
												PATHS.ERROR_CORRECTIONS_PAGE +
													'?responsibleUser=' +
													selectedUserRealId +
													'&status=Not Fixed',
											)
										}
										size="small"
										sx={{ m: '0px 0px 5px 5px' }}
									>
										<ArrowForward fontSize="small" color="primary" />
									</IconButton>
								)}
							</Typography>
							<Divider sx={{ my: 1 }} />
							<Typography
								variant="body1"
								color="primary"
								fontWeight="bold"
								sx={{ fontSize: '0.85rem' }}
							>
								Support Manager for merchants:{' '}
								<span style={{ fontSize: '1.2rem', marginLeft: '0.2rem' }}>
									{emManagementList?.totalCount ?? 0}
								</span>{' '}
								{(emManagementList?.totalCount ?? 0) > 0 && (
									<IconButton
										onClick={() =>
											navigate(PATHS.EM_MANAGEMENT_PAGE + '?supportManager=' + selectedUserRealId)
										}
										size="small"
										sx={{ m: '0px 0px 5px 5px' }}
									>
										<ArrowForward fontSize="small" color="primary" />
									</IconButton>
								)}
							</Typography>
						</Paper>
						<Paper
							sx={{
								display: 'flex',
								flexDirection: 'column',
								borderRadius: '12px',
								flex: 1,
								height: '100%',
							}}
						>
							<ConnectedEmerchants
								email={
									selectedUserIndex !== null ? users?.data[selectedUserIndex]?.email : undefined
								}
								source="userManagement"
							/>
						</Paper>
					</Box>
					<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
						<DragDropContext onDragEnd={onDragEnd}>
							<Paper
								sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderRadius: '12px' }}
							>
								<Box
									sx={{
										p: 0.2,
										backgroundColor: 'primary.main',
										color: 'primary.contrastText',
										borderRadius: '12px 12px 0 0',
										width: '100%',
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										fontSize: '0.85rem',
										fontWeight: 'bold',
									}}
								>
									Assigned Permissions
								</Box>
								<Droppable droppableId="assigned">
									{(provided, snapshot) => (
										<Box
											ref={provided.innerRef}
											{...provided.droppableProps}
											sx={{
												p: 2,
												display: 'flex',
												flexDirection: 'column',
												gap: 1,
												flexGrow: 1,
												backgroundColor: snapshot.isDraggingOver ? '#f0f0f0' : 'inherit',
												minHeight: '100px',
												borderRadius: '12px',
											}}
										>
											{assignedPermissions.map((role, index) => (
												<Draggable
													key={role.id.toString()}
													draggableId={role.id.toString()}
													index={index}
												>
													{(dragProvided) => (
														<div
															ref={dragProvided.innerRef}
															{...dragProvided.draggableProps}
															{...dragProvided.dragHandleProps}
															style={{ ...dragProvided.draggableProps.style }}
														>
															<PermitionItem
																title={role.roleNameUI}
																type="remove"
																onClick={() => handleRemoveClick(role)}
															/>
														</div>
													)}
												</Draggable>
											))}
											{provided.placeholder}
										</Box>
									)}
								</Droppable>
							</Paper>

							<Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
								<Box
									sx={{
										p: 0.2,
										backgroundColor: 'primary.main',
										color: 'primary.contrastText',
										borderRadius: '12px 12px 0 0',
										width: '100%',
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										fontSize: '0.85rem',
										fontWeight: 'bold',
									}}
								>
									Available Permissions
								</Box>
								<Droppable droppableId="available">
									{(provided, snapshot) => (
										<Box
											ref={provided.innerRef}
											{...provided.droppableProps}
											sx={{
												p: 2,
												display: 'flex',
												flexDirection: 'column',
												gap: 1,
												flexGrow: 1,
												backgroundColor: snapshot.isDraggingOver ? '#f0f0f0' : 'inherit',
												minHeight: '100px',
												borderRadius: '12px',
											}}
										>
											{availablePermissions.map((role, index) => (
												<Draggable key={role.roleName} draggableId={role.roleName} index={index}>
													{(dragProvided) => (
														<div
															ref={dragProvided.innerRef}
															{...dragProvided.draggableProps}
															{...dragProvided.dragHandleProps}
															style={{ ...dragProvided.draggableProps.style }}
														>
															<PermitionItem
																title={role.roleNameUI}
																type="add"
																onClick={() => handleAddClick(role)}
															/>
														</div>
													)}
												</Draggable>
											))}
											{provided.placeholder}
										</Box>
									)}
								</Droppable>
							</Paper>
						</DragDropContext>
					</Box>
				</Box>
			)}
		</Box>
	);
};
